// Compact running-timer indicator (architecture sprint-04 §5.6, S5.5; sprint 7 §6.6). Shown on every
// page and breakpoint while a timer exists: under the Logo in the sidebar (lg+) and in the slim header
// below lg. Stop uses the same flow as the hero.
import { Pause, Square } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTimerActions } from '@/components/timer/TimerActionsProvider';
import { Button } from '@/components/ui/button';
import { useActiveShift } from '@/hooks/use-shifts';
import { useTimer } from '@/hooks/use-timer';
import { cn } from '@/lib/cn';
import { shiftElapsedSeconds } from '@/lib/shift-format';
import type { ShiftSnapshot } from '@/types/shifts';
import { elapsedSeconds, formatElapsed } from '@/lib/timer-time';
import { useNowTick } from '@/lib/use-now';
import type { TimerSnapshot } from '@/types/timer';

const TICK_MS = 1000;

type IndicatorVariant = 'sidebar' | 'header';

export function TimerIndicatorSlot({ variant = 'header' }: { variant?: IndicatorVariant }): JSX.Element | null {
  const timer = useTimer();
  const shift = useActiveShift();
  if (timer.isLoading || timer.isError) return null;
  if (timer.data) return <TimerIndicator snapshot={timer.data} variant={variant} />;
  // Sprint 13 (§15): a timer and a shift never coexist, so the slot shows the open shift instead.
  if (shift?.shift) return <ShiftIndicator snapshot={shift} variant={variant} />;
  return null;
}

const LINK_FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function ShiftIndicator({ snapshot, variant }: { snapshot: ShiftSnapshot; variant: IndicatorVariant }): JSX.Element {
  const perfNow = useNowTick(TICK_MS);
  const elapsed = formatElapsed(shiftElapsedSeconds(snapshot, perfNow));
  const project = snapshot.shift?.project.name ?? '';
  const sidebar = variant === 'sidebar';
  return (
    <div className={sidebar ? 'mx-3 mb-2 flex items-center gap-1 rounded-lg bg-muted/50 py-1 pl-3 pr-1' : 'flex items-center gap-1'}>
      <Link
        to="/"
        aria-label={`On shift on ${project} ${elapsed}, open Dashboard`}
        className={cn(
          sidebar
            ? 'flex min-w-0 flex-1 items-center gap-2 rounded-md py-1 text-sm text-foreground'
            : 'flex min-w-0 items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-foreground hover:bg-accent',
          LINK_FOCUS,
        )}
      >
        <span aria-hidden="true" className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-success" />
        <span className="min-w-0 truncate text-xs text-muted-foreground">On shift · {project} ·</span>
        <span role="timer" className="font-mono tabular-nums">
          {elapsed}
        </span>
      </Link>
    </div>
  );
}

function TimerIndicator({ snapshot, variant }: { snapshot: TimerSnapshot; variant: IndicatorVariant }): JSX.Element {
  const actions = useTimerActions();
  const perfNow = useNowTick(TICK_MS);
  const paused = snapshot.timer.pausedAt !== null;
  const elapsed = formatElapsed(elapsedSeconds(snapshot, perfNow));
  const sidebar = variant === 'sidebar';

  return (
    <div className={sidebar ? 'mx-3 mb-2 flex items-center gap-1 rounded-lg bg-muted/50 py-1 pl-3 pr-1' : 'flex items-center gap-1'}>
      <Link
        to="/"
        aria-label={`Timer ${paused ? 'paused' : 'running'} ${elapsed}, open Dashboard`}
        className={cn(
          sidebar
            ? 'flex min-w-0 flex-1 items-center gap-2 rounded-md py-1 text-sm text-foreground'
            : 'flex min-w-0 items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-foreground hover:bg-accent',
          LINK_FOCUS,
        )}
      >
        {/* The original's recording dot (A-7): red pulsing while running, grey while paused. */}
        <span aria-hidden="true" className={cn('h-2 w-2 shrink-0 rounded-full', paused ? 'bg-faint' : 'animate-pulse bg-danger')} />
        <span role="timer" className="font-mono tabular-nums">
          {elapsed}
        </span>
        {paused &&
          (sidebar ? (
            <span className="text-xs text-warning-fg">Paused</span>
          ) : (
            <span className="flex items-center gap-0.5 text-warning-fg">
              <Pause className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="hidden text-xs sm:inline">Paused</span>
            </span>
          ))}
      </Link>
      <Button
        variant="ghost"
        size={sidebar ? 'icon-sm' : 'icon'}
        className={sidebar ? undefined : 'h-11 w-11 sm:h-9 sm:w-9'}
        aria-label="Stop timer"
        onClick={actions.requestStop}
        disabled={actions.pending}
      >
        <Square />
      </Button>
    </div>
  );
}
